import { supabaseClient } from './supabase';
import { logger } from './logger';
import type { NavigationItem } from '../types/shared/navigation';
import { HOTEL_ID } from './supabase/configClient';

class NavigationClient {
  private static instance: NavigationClient;
  private navigationCache: NavigationItem[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000;
  private isLoading: boolean = false;
  private loadPromise: Promise<NavigationItem[]> | null = null;
  private retryCount: number = 0;
  private readonly MAX_RETRIES = 5;
  private readonly BASE_RETRY_DELAY = 1000;

  private constructor() {}

  public static getInstance(): NavigationClient {
    if (!NavigationClient.instance) {
      NavigationClient.instance = new NavigationClient();
    }
    return NavigationClient.instance;
  }

  private isCacheValid(): boolean {
    return (
      this.navigationCache !== null &&
      Date.now() - this.cacheTimestamp < this.CACHE_DURATION
    );
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async retryWithBackoff<T>(operation: () => Promise<T>): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        if (!navigator.onLine) throw new Error('Browser is offline');

        if (attempt > 0) {
          const delayMs = this.BASE_RETRY_DELAY * Math.pow(2, attempt - 1);
          await this.delay(delayMs);
        }

        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        logger.warn(`Navigation fetch attempt ${attempt + 1} failed`, {
          category: 'Navigation',
          data: { error: lastError }
        });

        if (attempt === this.MAX_RETRIES) throw lastError;
      }
    }

    throw lastError!;
  }

  public async getNavigation(): Promise<NavigationItem[]> {
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    if (this.isCacheValid()) {
      return this.navigationCache!;
    }

    try {
      this.isLoading = true;
      this.loadPromise = this.fetchNavigation();
      const navigation = await this.loadPromise;
      return navigation;
    } catch (error) {
      logger.error('Error loading navigation', {
        category: 'Navigation',
        data: {
          error,
          errorDetails: error instanceof Error ? { message: error.message, stack: error.stack } : error
        }
      });

      if (this.navigationCache) {
        logger.warn('Returning expired navigation cache due to fetch error', {
          category: 'Navigation',
          data: {
            cacheAge: Math.round((Date.now() - this.cacheTimestamp) / 1000) + 's',
            itemCount: this.navigationCache.length
          }
        });
        return this.navigationCache;
      }

      return [];
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  private async fetchNavigation(): Promise<NavigationItem[]> {
    const { data, error } = await this.retryWithBackoff(async () => {
      const response = await supabaseClient
        .from('hotels_navigation')
        .select('*')
        .eq('hotel_id', HOTEL_ID)
        .order('order_index');

      if (response.error) throw response.error;
      return response;
    });

    if (error) throw error;

    const filteredData = data.filter(record => record.use === true);
    this.navigationCache = filteredData.map(record => ({
      id: record.id,
      description_fr: record.description_fr || '',
      type_rubrique: record.sectiontype,
      section_id: record.section_id,
      icon: record.icon_metadata?.url,
      replace_img: record.replace_img_metadata?.url ? [{ url: record.replace_img_metadata.url }] : undefined,
      names: {
        fr: record.name_fr,
        en: record.name_en || record.name_fr,
        es: record.name_es || record.name_fr,
        it: record.name_it || record.name_fr,
        pt: record.name_pt || record.name_fr,
        de: record.name_de || record.name_fr,
        nl: record.name_nl || record.name_fr,
        ja: record.name_ja || record.name_fr,
        zh: record.name_zh || record.name_fr,
        ru: record.name_ru || record.name_fr
      },
      fulladress: record.fulladress,
      url: record.url,
      urlLivechat: record.urllivechat,
      API: record.api === 'ticketmaster' ? 'ticketmaster' : 'airtable',
      use_img: record.use_img,
      include_in_explore: record.include_in_explore !== false,
      allow_reception_chat: record.allow_reception_chat === true
    }));

    this.cacheTimestamp = Date.now();

    return this.navigationCache;
  }

  public generateSlug(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  public getRouteFromType(type: NavigationItem['type_rubrique']): string {
    return type;
  }

  public getSectionNameFromSlug(items: NavigationItem[], slug: string): string {
    const item = items.find(item => this.generateSlug(item.names.fr) === slug);
    return item ? this.generateSlug(item.names.fr) : slug;
  }

  /**
   * Construit une URL de section
   * @param sectionSlug Le slug de la section
   * @returns L'URL relative de la section
   */
  public buildSectionUrl(sectionSlug: string): string {
    // Retourne simplement le slug préfixé par un /
    // BrowserRouter avec basename gère le reste
    return `/${sectionSlug}`;
  }

  /**
   * Construit une URL d'item dans une section
   * @param sectionSlug Le slug de la section
   * @param itemSlug Le slug de l'item
   * @returns L'URL relative de l'item
   */
  public buildItemUrl(sectionSlug: string, itemSlug: string): string {
    // Construit l'URL relative de l'item
    return `/${sectionSlug}/${itemSlug}`;
  }

  public clearCache(): void {
    this.navigationCache = null;
    this.cacheTimestamp = 0;
    this.isLoading = false;
    this.loadPromise = null;
    this.retryCount = 0;
  }
}

export const navigationClient = NavigationClient.getInstance();